import { ReactNode, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { spaceGrotesk } from '../theme/fonts';
import { focusInset } from '../theme/themes';
import { byxScheme } from '../theme/tokens';
import { useByxT } from '../shared/translations';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 3, 1.5),
  },
  panel: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minHeight: 56,
    boxSizing: 'border-box',
    padding: theme.spacing(1, 1.25, 1, 2.25),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 14,
  },
  icon: {
    display: 'grid',
    placeItems: 'center',
    color: theme.palette.text.secondary,
    fontSize: 20,
    '& svg': { fontSize: 'inherit' },
  },
  trail: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  trailList: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5, 1),
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  trailItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  ancestor: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  separator: {
    fontSize: 16,
    color: theme.palette.text.secondary,
  },
  // Title and the optional subtitle on one row (Busca: "<n> resultados").
  titleRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1.5),
    '& > h1': { flex: '0 1 auto', minWidth: 0 },
  },
  subtitle: {
    flex: '0 1 auto',
    minWidth: 0,
    '&:empty': { display: 'none' },
  },
  title: {
    margin: 0,
    fontFamily: spaceGrotesk,
    fontSize: 16,
    fontWeight: 700,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  actions: {
    marginLeft: 'auto',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  tabs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0, 1.5),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 14,
  },
  // Page-section tabs (design system Tabs): label 14px 600 in muted-text,
  // the current one in ink over a 2px ink indicator.
  tab: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 48,
    padding: theme.spacing(0, 1.25),
    fontFamily: spaceGrotesk,
    fontSize: 14,
    lineHeight: '20px',
    color: theme.palette.text.secondary,
    fontWeight: 600,
    '&:hover': { color: theme.palette.text.primary },
    // Keyboard focus (R9-3): the 2px primary ring inside the tab, the
    // design system rule for link-based tabs, in place of the browser's.
    '&:focus-visible': focusInset(byxScheme(theme.palette.type)),
  },
  tabSelected: {
    color: theme.palette.text.primary,
    boxShadow: `inset 0 -2px 0 ${theme.palette.text.primary}`,
  },
}));

export type PageHeaderCrumb = { label: string; href: string };
export type PageHeaderTab = { id: string; label: string; href: string };

export type PageHeaderProps = {
  title: string;
  /** Where the title leads (the plugin's root page), as in the stock header. */
  titleLink?: string;
  icon?: ReactNode;
  /** Trail from the page root to the current page; the last one is current. */
  breadcrumbs?: PageHeaderCrumb[];
  actions?: ReactNode[];
  tabs?: PageHeaderTab[];
  /** Receives the element after the title where a page subtitle goes. */
  subtitleRef?: (element: HTMLElement | null) => void;
};

/**
 * The tab whose href is the longest prefix of the current path, so nested
 * routes keep their tab selected without a parent tab matching everything.
 */
export function findSelectedTab(tabs: PageHeaderTab[], pathname: string) {
  return tabs
    .filter(tab => pathname === tab.href || pathname.startsWith(`${tab.href}/`))
    .sort((a, b) => b.href.length - a.href.length)[0];
}

/**
 * Page header from the "Backstage BYX" v2 design: a rounded panel with the
 * page icon, the title (or the breadcrumb trail on nested pages) and the
 * page actions, plus the page tabs in a strip of their own.
 */
export function PageHeader(props: Readonly<PageHeaderProps>) {
  const {
    title,
    titleLink,
    icon,
    breadcrumbs = [],
    actions = [],
    tabs = [],
    subtitleRef,
  } = props;
  const classes = useStyles();
  const { t } = useByxT();
  const { pathname } = useLocation();
  const rootRef = useRef<HTMLDivElement>(null);

  // BUI layouts size sticky elements from the header height, as with the
  // stock plugin header.
  useLayoutEffect(() => {
    const element = rootRef.current;
    if (!element) {
      return undefined;
    }
    const style = document.documentElement.style;
    const apply = () =>
      style.setProperty('--bui-header-height', `${element.offsetHeight}px`);
    apply();
    if (typeof ResizeObserver === 'undefined') {
      return () => style.removeProperty('--bui-header-height');
    }
    const observer = new ResizeObserver(apply);
    observer.observe(element);
    return () => {
      observer.disconnect();
      style.removeProperty('--bui-header-height');
    };
  }, []);

  const ancestors = breadcrumbs.slice(0, -1);
  const current = breadcrumbs.at(-1)?.label ?? title;
  const selectedTab = findSelectedTab(tabs, pathname);
  const visibleActions = actions.filter(Boolean);

  return (
    <div ref={rootRef} className={classes.root}>
      <header className={classes.panel}>
        {icon && (
          <span className={classes.icon} aria-hidden="true">
            {icon}
          </span>
        )}
        {ancestors.length > 0 ? (
          <nav aria-label={t('nav.pageHeader.trail')} className={classes.trail}>
            <ol className={classes.trailList}>
              {ancestors.map(crumb => (
                <li key={crumb.href} className={classes.trailItem}>
                  <Link
                    to={crumb.href}
                    underline="none"
                    className={classes.ancestor}
                  >
                    {crumb.label}
                  </Link>
                  <ChevronRightIcon
                    className={classes.separator}
                    aria-hidden="true"
                  />
                </li>
              ))}
              <li className={classes.trailItem} aria-current="page">
                <h1 className={classes.title}>{current}</h1>
              </li>
            </ol>
          </nav>
        ) : (
          <div className={`${classes.trail} ${classes.titleRow}`}>
            <h1 className={classes.title}>
              {/* Linked only when it leads elsewhere, e.g. from an entity
                  page back to the catalog. */}
              {titleLink && titleLink !== pathname ? (
                <Link to={titleLink} underline="hover" color="inherit">
                  {current}
                </Link>
              ) : (
                current
              )}
            </h1>
            {subtitleRef && (
              <span ref={subtitleRef} className={classes.subtitle} />
            )}
          </div>
        )}
        {visibleActions.length > 0 && (
          <div className={classes.actions}>{visibleActions}</div>
        )}
      </header>
      {tabs.length > 0 && (
        <nav aria-label={t('nav.pageHeader.sections')} className={classes.tabs}>
          {tabs.map(tab => {
            const isSelected = tab.id === selectedTab?.id;
            return (
              <Link
                key={tab.id}
                to={tab.href}
                underline="none"
                aria-current={isSelected ? 'page' : undefined}
                className={`${classes.tab} ${
                  isSelected ? classes.tabSelected : ''
                }`}
              >
                {tab.label}
              </Link>
            );
          })}
        </nav>
      )}
    </div>
  );
}
